import {Component, FontAwesomeIcon} from 'substance'

import {KeybindingCaptureComponent} from './KeybindingCaptureComponent'
import {KBDKeyComboComponent} from './KBDKeyComboComponent'

/**
 * @typedef Component.KeybindingEditorComponent.Keybinding
 * @property {string} combo - The key combination
 * @property {{}} spec
 * @property {string} spec.command - The command that is run when executed
 * @property {Object} [spec.type] - The key combination
 * @property {Boolean} globalCombo - If true, the keybinding is handled by NPKeyboardManager, othersiwe substance
 * @property {string} description='' - Human readable description of the keybinding
 */

/**
 * @typedef Component.KeybindingEditorComponent.State
 * @property {Component.KeybindingEditorComponent.Keybinding[]} keybindings
 * @property {Component.KeybindingEditorComponent.Keybinding} keybindingBeingEdited - If null, no
 */

/**
 * A component that lets the user view (and edit) keybindings configured in the writer
 *
 * @property {Component.KeybindingEditorComponent.State} state
 */
class KeybindingEditorComponent extends Component {

    /**
     * @returns {Component.KeybindingEditorComponent.State}
     */
    getInitialState() {
        return {
            keybindings: this.getKeybindings(),
            keybindingBeingEdited: null
        }
    }

    /**
     * @returns {Component.KeybindingEditorComponent.Keybinding[]}
     */
    getKeybindings() {
        // When keybinding editing is implemented this should load from localstorage
        // Or maybe npKeyboardManager should handle that?
        const {configurator} = this.context.api
        return configurator.dwKeyboardManager.rawKeybindings
    }

    render($$) {
        return $$('div', {class: 'keybinding-editor-container'}, [
            this._renderKeybindingCapture($$),
            this._renderKeybindingTable($$)
        ])
    }

    _renderKeybindingCapture($$) {
        const keybinding = this.state.keybindingBeingEdited

        if (!keybinding) {
            return null
        } else {
            return $$(KeybindingCaptureComponent, {
                keyCombo: keybinding.combo,
                description: this._getKeybindingDescription(keybinding),
                onSave: this._onCapturedKeybindingSave.bind(this),
                onCancel: this._onCapturedKeybindingCancel.bind(this)
            })
        }
    }

    /**
     * Callback when KeybindingCaptureComponent is saved
     * @param {string} newKeyCombo - The new captured keybinding
     */
    _onCapturedKeybindingSave(newKeyCombo) {
        const keybinding = this.state.keybindingBeingEdited
        console.info('Saving new combo for:', keybinding.spec.command, newKeyCombo)
        this.extendState({ keybindingBeingEdited: null })
    }

    /**
     * Callback when KeybindingCaptureComponent is canceled
     */
    _onCapturedKeybindingCancel() {
        this.extendState({ keybindingBeingEdited: null })
    }

    _renderKeybindingTable($$) {
        const tableClasses = ['keybinding-table']
        if (this.state.keybindingBeingEdited) { tableClasses.push('editing') }

        return $$('table', {class: tableClasses.join(' ')}, [
            this._renderTableHeader($$),
            this.state.keybindings.map(keybinding => this._renderKeybindingRow($$, keybinding))
        ])
    }

    /**
     * Renders the keybinding table header with descriptions of all columns
     * Should be localized with getLabel, but as it's not used right now it's not a problem
     * @todo remove if it isn't used
     */
    _renderTableHeader($$) {
        return $$('tr', {class: 'keybinding-row header'}, [
            $$('th', {class: 'edit'}),
            $$('th', {class: 'description'}, 'Description'),
            $$('th', {class: 'keybinding'}, 'Keybinding'),
            $$('th', {class: 'global'}, 'Global'),
            $$('th', {class: 'command'}, 'Command')
        ])
    }

    /**
     * Renders a single row for the table
     * @param {createElement} $$
     * @param {Component.KeybindingEditorComponent.Keybinding} keybinding
     */
    _renderKeybindingRow($$, keybinding) {
        const rowClasses = ['keybinding-row', 'body']
        if (keybinding.isCustom) { rowClasses.push('custom') }

        return $$('tr', {class: 'keybinding-row body'}, [

            // Editing disabled for now
            $$('td', {class: 'edit'},
                this._renderEditButton($$, keybinding)
            ).css('display', 'none'),

            $$('td', {class: 'description'},
                this._getKeybindingDescription(keybinding)
            ),

            $$('td', {class: 'keybinding'},
                $$(KBDKeyComboComponent, { keyCombo: keybinding.combo })
            ),

            $$('td', {class: 'global'},
                keybinding.globalCombo ? 'Yes' : 'No'
            ),

            $$('td', {class: 'command'},
                keybinding.spec.command
            )
        ])
    }

    _renderEditButton($$, keybinding) {
        return $$('button', null, [
            $$(FontAwesomeIcon, {icon: 'fa-pencil'})
        ]).on('click', () => this._onEditButtonClick(keybinding))
    }

    _onEditButtonClick(keybinding) {
        console.info('User wants to edit command', keybinding.spec.command)
        if (!this.state.keybindingBeingEdited) {
            this.extendState({ keybindingBeingEdited: keybinding })
        }
    }

    _getKeybindingDescription(keybinding) {
        const {api} = this.context
        if (keybinding.description) {
            return api.getLabel(keybinding.description)
        } else if (keybinding.spec && keybinding.spec.command) {
            return api.getLabel(keybinding.spec.command)
        }
        return api.getLabel('keybinding-no-description-provided')
    }

    /**
     * onClose method from dialog component
     * @param {string} status
     */
    onClose(status) {
        if (status === 'save') {
            console.info('Closing and saving')
        }
        return true
    }
}

export default KeybindingEditorComponent
